import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import {
  GET_HARDWARE_CONFIG_HISTORY,
  GetHardwareConfigHistoryData,
  GetHardwareConfigHistoryVars,
  HardwareConfigHistoryEntry,
} from '../graphql/querys/getHardwareConfigHistory';

interface DeviceConfigHistoryPanelProps {
  deviceId: string;
  open: boolean;
  onSelectVersion: (entry: HardwareConfigHistoryEntry, entries: HardwareConfigHistoryEntry[], idx: number) => void;
  onRestoreCurrent: () => void;
  currentEntryId?: string;
}

const formatActionType = (actionType: string): string => {
  if (actionType === 'createDeviceHardwareConfiguration') return 'Creó';
  if (actionType === 'updateDeviceHardwareConfiguration') return 'Actualizó';
  return actionType;
};

const DeviceConfigHistoryPanel = ({ deviceId, open, onSelectVersion, onRestoreCurrent, currentEntryId }: DeviceConfigHistoryPanelProps) => {
  const { data, loading } = useQuery<GetHardwareConfigHistoryData, GetHardwareConfigHistoryVars>(
    GET_HARDWARE_CONFIG_HISTORY,
    {
      variables: { deviceId },
      skip: !open,
    },
  );

  if (!open) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const entries = data?.device?.profile?.hardwareConfigHistory?.entries ?? [];

  if (entries.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sin historial de cambios
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#1e2a35' }}>
      <List dense disablePadding>
        {entries.map((entry, index) => {
          const isLatest = index === 0;
          const isViewing = entry.id === currentEntryId;
          // entries viene ordenado de mas reciente a mas antiguo (index 0 = actual);
          // el numero de version crece con la antiguedad inversa: v1 = mas antigua, vN = actual.
          const versionNumber = entries.length - index;
          const dateLabel = new Date(Number(entry.timestamp)).toLocaleString('es-CL');
          const actionLabel = formatActionType(entry.actionType);
          const responsibleLabel = entry.responsibleDisplayName;

          return (
            <ListItem
              key={entry.id}
              disablePadding
              secondaryAction={
                isViewing ? (
                  <Chip label="Seleccionada" size="small" color="info" variant="outlined" />
                ) : undefined
              }
            >
              <ListItemButton
                selected={isViewing}
                onClick={() => (isLatest ? onRestoreCurrent() : onSelectVersion(entry, entries, index))}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" component="div">
                      v{versionNumber} — {dateLabel}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" component="div">
                      {actionLabel} {responsibleLabel}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default DeviceConfigHistoryPanel;
